import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import { Carousel } from "@domain/carousel/carousel";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { Group } from "@domain/group/group";
import { ContentLicense } from "@domain/licence/contentLicense";
import { Article } from "@domain/lms/article/article";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { Course, CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { Quiz } from "@domain/lms/quiz/quiz";
import { Organization } from "@domain/organization/organization";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { Scoring } from "@domain/ranking/ranking";
import { useSearchResult } from "@domain/search/hook";
import { SearchDomain } from "@domain/search/searchDomain";
import { Profile } from "@domain/user/profile";
import { ArticleThumbnail } from "@ui/components/article/articleThumbnail";
import { Secondary } from "@ui/components/containers/textBlocks";
import { CourseThumbnail } from "@ui/components/course/courseThumbnail";
import { DiscussionThumbnail } from "@ui/components/group/discussionThumbnail";
import { ShareroomThumbnail } from "@ui/components/group/shareroomThumbnail";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Stack } from "@ui/components/layout";
import { CarouselThumbnail } from "@ui/components/pdfCarousel/carouselThumbnail";
import { SuggestedProfileThumbnail } from "@ui/components/profile/suggestedUserThumbnail";
import { QuizThumbnail } from "@ui/components/quiz/quizThumbnail";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { useSearchTracking } from "./hooks";
import { OrganizationThumbnail } from "./organizationThumbnail";

interface SearchResultsProps extends React.HTMLAttributes<HTMLDivElement> {
	query: string;
	domain: SearchDomain;
	categories?: Category[];
	levels?: ContentLevel[];
	languages?: ContentLanguage[];
	durations?: CourseDuration[];
	providers?: CourseProvider[];
	orgTypes?: OrganizationType[];
	orgSubTypes?: OrganizationSubType[];
	countries?: CountryCode[];
	license?: ContentLicense[];
	scoring?: Scoring[];
	rating?: number;
}
export const SearchResults: React.FC<SearchResultsProps> = ({
	query,
	domain,
	categories,
	levels,
	languages,
	durations,
	providers,
	orgTypes,
	orgSubTypes,
	countries,
	license,
	scoring,
	rating,
	...htmlProps
}) => {
	const [loading, setLoading] = useState(false);
	const {
		result: searchResults,
		loading: resultsLoading,
		fetchMore,
		totalElements,
		lastPage,
	} = useSearchResult(
		query,
		domain,
		categories,
		levels,
		languages,
		durations,
		providers,
		orgTypes,
		orgSubTypes,
		countries,
		license,
		scoring,
		rating
	);

	useEffect(() => {
		setLoading(true);
		setTimeout(() => setLoading(false), 300);
	}, [
		query,
		domain,
		categories,
		levels,
		languages,
		durations,
		providers,
		orgTypes,
		orgSubTypes,
		countries,
		license,
		scoring,
		rating,
	]);

	useSearchTracking(query, domain);
	return (
		<div {...htmlProps}>
			<InfiniteScroll onEndReached={fetchMore} loading={resultsLoading}>
				{!!totalElements && (
					<Title>
						{domain !== "USER" && (
							<b>
								{I18n.t(`search.${orgTypes && orgTypes.length === 1 ? orgTypes[0] : domain}.count`, {
									count: totalElements,
								})}
							</b>
						)}

						{query || license ? (
							<>
								{" "}
								{I18n.t("search.for")}{" "}
								<em>
									<b>{query ? query : license ? I18n.t(`search.filter.license.${license}`) : null}</b>
								</em>
							</>
						) : null}

						{query || categories?.length === 1 ? (
							<>
								{" "}
								{I18n.t("search.for")}{" "}
								<em>
									<b>{query ? query : categories ? I18n.t(`category.${categories[0]}`) : null}</b>
								</em>
							</>
						) : null}
					</Title>
				)}
				<Content threeColumns={domain === SearchDomain.User}>
					{resultsLoading || loading ? (
						<ClipLoader />
					) : totalElements === 0 ? (
						<Secondary>{I18n.t("search.empty")}</Secondary>
					) : (
						searchResults?.map((result) => {
							return result ? (
								domain === SearchDomain.User ? (
									<SuggestedProfileThumbnail key={result.id} profile={result as Profile} />
								) : domain === SearchDomain.Course ? (
									<CourseThumbnail key={result.id} course={result as Course} />
								) : domain === SearchDomain.Article ? (
									<ArticleThumbnail key={result.id} article={result as Article} />
								) : domain === SearchDomain.Quiz ? (
									<QuizThumbnail key={result.id} quiz={result as Quiz} />
								) : domain === SearchDomain.Carousel ? (
									<CarouselThumbnail key={result.id} carousel={result as Carousel} big />
								) : domain === SearchDomain.Organization ? (
									<OrganizationThumbnail key={result.id} organization={result as Organization} />
								) : domain === SearchDomain.Shareroom ? (
									<ShareroomThumbnail key={result.id} shareroom={result as Group} />
								) : (
									<DiscussionThumbnail key={result.id} discussion={result as Group} />
								)
							) : (
								<></>
							);
						})
					)}
				</Content>
				{!lastPage && !!totalElements && (
					<Stack align="center" style={{ marginTop: 20 }}>
						<ClipLoader size={48} />
					</Stack>
				)}
			</InfiniteScroll>
		</div>
	);
};
const Title = styled.div`
	margin-top: 11px;
	margin-bottom: 13px;
`;

const Content = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "threeColumns",
})<{ threeColumns?: boolean }>`
	@media ${mobile} {
		${gap(16)};
	}
	@media ${desktop} {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-gap: 20px;
	}
	${({ threeColumns }) =>
		threeColumns &&
		css`
			@media (min-width: 1200px) {
				grid-template-columns: repeat(3, 1fr);
				margin-top: 43px;
			}
		`}
`;
